import {
    useGetPayoutSwiftTracking,
    useGetPayoutTracking,
} from 'api/payout-tracking';
import {useLanguage} from 'context/language-context';
import {useHistory, useParams} from 'react-router';
import {HashParamType} from 'types/common';
import {formatDateTime, formatDateWithoutTZConversion} from 'helpers/dates';
import {translate} from 'helpers/localize';
import {formatNumber} from 'helpers/common';
import {ITimelineEntry} from 'components/ui/PaymentTimeline/PaymentTimeline';
import {useGetExportFile} from 'api/fileExport';
import {useNotification} from 'context/notification-context';

const getArrivedText = (swift: boolean) => {
    if (swift) {
        return 'The money has been received by the bank. Depending on the local bank, it may take a few hours to appear on your account.';
    }
    return 'The money is expected to be credited to the bank account soon. Depending on the local bank, it may take a few hours to appear on your account.';
};

const usePaymentTrackingPublicPage = () => {
    const history = useHistory();
    const {hash} = useParams<HashParamType>();
    const {selectedLanguageParams} = useLanguage();
    const {data, isLoading} = useGetPayoutTracking(hash);
    const {data: swiftTracking} = useGetPayoutSwiftTracking(
        hash,
        !!data?.is_swift_trackable,
    );
    const {mutate: downloadFile, isPending: isDownloadLoading} =
        useGetExportFile();
    const {addNotification} = useNotification();

    const entries: ITimelineEntry[] = [];

    const pastEstimatedArrival =
        data &&
        data.estimated_time_arrival &&
        new Date(data.estimated_time_arrival).getTime() < new Date().getTime();

    if (data && !Array.isArray(data)) {
        entries.push({
            comment: translate(
                '%{businessPartner} set up the transfer of %{amount}',
                {
                    businessPartner: data.business_partner.company_name,
                    amount: `${formatNumber(data.amount)} ${data.currency
                        .split('/')
                        .pop()}`,
                },
            ),
            status: 'done',
            label: formatDateTime(data.created_at || new Date()),
        });

        const receivedStatus =
            data.wallet_executed_at &&
            new Date(data.wallet_executed_at).getTime() < new Date().getTime()
                ? 'done'
                : 'pending';

        entries.push(
            {
                label: data.wallet_executed_at
                    ? formatDateTime(data.wallet_executed_at)
                    : translate('Pending'),
                comment:
                    receivedStatus === 'pending'
                        ? translate(
                              'Receiving %{amount} from %{businessPartner}',
                              {
                                  amount: `${formatNumber(
                                      data.amount,
                                  )} ${data.currency.split('/').pop()}`,
                                  businessPartner:
                                      data.business_partner.company_name,
                              },
                          )
                        : translate(
                              'We received %{amount} from %{businessPartner}',
                              {
                                  businessPartner:
                                      data.business_partner.company_name,
                                  amount: `${formatNumber(
                                      data.amount,
                                  )} ${data.currency.split('/').pop()}`,
                              },
                          ),
                status: receivedStatus,
            },
            {
                label: data.transferred_at
                    ? formatDateWithoutTZConversion(data.transferred_at)
                    : translate('Pending'),
                comment: translate(
                    !data.transferred_at ||
                        new Date(data.transferred_at).getTime() >
                            new Date().getTime()
                        ? 'Transferring to the bank'
                        : 'Your money’s on its way',
                ),
                status:
                    !data.transferred_at ||
                    new Date(data.transferred_at).getTime() >
                        new Date().getTime()
                        ? 'pending'
                        : 'done',
            },
            {
                label: formatDateWithoutTZConversion(
                    data.estimated_time_arrival,
                ),
                comment: translate(
                    pastEstimatedArrival
                        ? getArrivedText(data?.is_swift_trackable)
                        : 'Expected time of arrival',
                ),
                status: pastEstimatedArrival ? 'done' : 'pending',
            },
        );
    }

    const handleDownloadMT103 = () => {
        downloadFile(
            {
                url: `/web_api/payout_tracking_public/${hash}/mt103`,
            },
            {
                onError: (error: any) => {
                    addNotification(
                        {
                            area: 'public-tracking',
                            message:
                                error?.['hydra:description'] ||
                                'Something went wrong',
                            status: 'error',
                        },
                        'mt103-download',
                    );
                },
            },
        );
    };

    return {
        data,
        isLoading,
        selectedLanguageParams,
        swiftTracking,
        entries,
        pastEstimatedArrival,
        history,
        handleDownloadMT103,
        isDownloadLoading,
    };
};

export default usePaymentTrackingPublicPage;
